import { motion } from 'framer-motion';
import { Check, Sparkles, Star } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';

export default function SectionOptions({ data }) {
  if (!data) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <section id="options" className="section-padding bg-secondary-950">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            <Sparkles className="inline-block mr-3 text-primary-500" size={40} />
            {data.title}
          </h2>
          <p className="text-xl text-primary-500 font-semibold mb-2">{data.subtitle}</p>
          <p className="text-secondary-200 max-w-3xl mx-auto">{data.description}</p>
        </motion.div>

        {/* Packs Options */}
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
          {data.packs.map((pack, index) => (
            <motion.div
              key={pack.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                variant={pack.recommended ? 'gradient' : 'default'}
                className={`h-full flex flex-col ${
                  pack.recommended
                    ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-black'
                    : ''
                }`}
              >
                {/* Badge recommandé */}
                {pack.recommended && (
                  <div className="flex justify-center -mt-3 mb-4">
                    <span className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                      <Star size={16} fill="currentColor" />
                      Recommandé
                    </span>
                  </div>
                )}

                {/* Header */}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2 text-white">{pack.name}</h3>

                  {/* Prix */}
                  <div className="mb-3">
                    {pack.originalPrice && (
                      <div className="text-secondary-400 line-through text-sm">
                        {formatPrice(pack.originalPrice)}
                      </div>
                    )}
                    <div className="text-4xl font-bold text-primary-500">
                      {formatPrice(pack.price)}
                    </div>
                    {pack.saving && (
                      <div className="text-green-400 text-sm font-semibold mt-1">
                        Économie de {formatPrice(pack.saving)}
                      </div>
                    )}
                  </div>

                  {/* Durée */}
                  <div className="text-secondary-300 text-sm">
                    Réalisation : {pack.duration}
                  </div>
                </div>

                {/* Description */}
                <p className="text-secondary-200 text-sm mb-6 italic">{pack.description}</p>

                {/* Livrables */}
                <div className="flex-1 mb-6">
                  <h4 className="text-white font-semibold mb-3 text-sm uppercase tracking-wide">
                    Livrables :
                  </h4>
                  <ul className="space-y-2">
                    {pack.livrables.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="text-primary-500 flex-shrink-0 mt-0.5" size={16} />
                        <span className="text-secondary-200">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Highlight */}
                {pack.highlight && (
                  <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-3 mb-4">
                    <p className="text-primary-300 text-xs font-medium">{pack.highlight}</p>
                  </div>
                )}

                {/* CTA */}
                <Button
                  variant={pack.recommended ? 'primary' : 'outline'}
                  fullWidth
                  onClick={() => {
                    // Scroll to contact section
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {pack.recommended ? 'Choisir ce pack' : 'En savoir plus'}
                </Button>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Sur mesure */}
        {data.surMesure && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <Card>
              <h3 className="text-2xl font-bold text-white mb-2">{data.surMesure.title}</h3>
              <p className="text-secondary-300 mb-4">{data.surMesure.description}</p>

              <div className="grid gap-3">
                {data.surMesure.items.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between bg-secondary-800 rounded-lg p-4"
                  >
                    <span className="text-white font-medium">{item.format}</span>
                    <span className="text-primary-500 text-xl font-bold">
                      {formatPrice(item.price)}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}
      </div>
    </section>
  );
}
